import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class TabsController extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  /** @type {HTMLElement | null} */
  #tablist = null;
  /** @type {HTMLElement | null} */
  #viewAll = null;
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    const section = this.closest('[data-tabs-root]');
    this.#tabs = Array.from(this.querySelectorAll('[data-tabs-tab]'));
    this.#panels = Array.from(section?.querySelectorAll('[data-tabs-panel]') ?? []);
    this.#tablist = this.querySelector('[data-tabs-list]');

    if (!this.#tabs.length || !this.#panels.length) return;

    this.#viewAll = section?.querySelector('[data-tabs-link]') ?? null;

    this.#active = Math.max(
      0,
      this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    for (const [index, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.#activate(index), opts);
      tab.addEventListener('keydown', (event) => this.#onTabKeydown(event, index), opts);
    }

    for (const [index, panel] of this.#panels.entries()) {
      panel.addEventListener('keydown', (event) => this.#onPanelKeydown(event, index), opts);
    }

    mediaQueryLarge.addEventListener('change', () => this.#updateTablistOrientation(), opts);

    this.#updateTablistOrientation();
    this.#updateUI();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #isVerticalOrientation() {
    if (!this.#tablist) return false;

    if (this.#tablist.dataset.layoutOrientation === 'vertical') return true;

    return this.#tablist.dataset.verticalOnMobile === 'true' && isMobileBreakpoint();
  }

  #updateTablistOrientation() {
    if (!this.#tablist) return;

    this.#tablist.setAttribute(
      'aria-orientation',
      this.#isVerticalOrientation() ? 'vertical' : 'horizontal'
    );
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onTabKeydown(event, index) {
    if (event.key === 'Tab' && !event.shiftKey && index === this.#active) {
      const panel = this.#panels[this.#active];
      const focusable = panel ? this.#getFocusableElements(panel) : [];

      if (focusable.length > 0) {
        event.preventDefault();
        focusable[0].focus();
        return;
      }
    }

    const isVertical = this.#isVerticalOrientation();
    /** @type {Record<string, number>} */
    const navMap = {
      [isVertical ? 'ArrowUp' : 'ArrowLeft']: -1,
      [isVertical ? 'ArrowDown' : 'ArrowRight']: 1,
      Home: -index,
      End: this.#tabs.length - 1 - index,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (index + offset + this.#tabs.length) % this.#tabs.length;
    this.#focusTab(nextIndex);
    this.#activate(nextIndex);
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onPanelKeydown(event, index) {
    if (event.key !== 'Tab' || !event.shiftKey || index !== this.#active) return;

    const panel = this.#panels[index];
    if (!panel) return;

    const focusable = this.#getFocusableElements(panel);
    const firstFocusable = focusable[0];
    const isAtStart =
      (firstFocusable && document.activeElement === firstFocusable) ||
      (!focusable.length && document.activeElement === panel);

    if (isAtStart) {
      event.preventDefault();
      this.#tabs[this.#active]?.focus();
    }
  }

  /**
   * @param {number} index
   */
  #focusTab(index) {
    const tab = this.#tabs[index];
    if (!tab) return;

    tab.focus();
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement[]}
   */
  #getFocusableElements(panel) {
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => !el.closest('[inert]'))
      .map((el) => /** @type {HTMLElement} */ (el));
  }

  /** @param {number} index */
  #activate(index) {
    if (index < 0 || index >= this.#tabs.length || index === this.#active) return;
    this.#active = index;
    this.#updateUI();
  }

  #updateUI() {
    for (const [index, tab] of this.#tabs.entries()) {
      const isActive = index === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, panel] of this.#panels.entries()) {
      const isActive = index === this.#active;
      panel.hidden = !isActive;
      panel.toggleAttribute('inert', !isActive);
    }

    if (this.#viewAll) {
      const activeTab = this.#tabs[this.#active];
      const collectionUrl = activeTab?.dataset.collectionUrl ?? '';
      const productsCount = Number(activeTab?.dataset.productsCount ?? 0);
      const maxProducts = Number(this.dataset.maxProducts ?? 0);
      const link =
        this.#viewAll instanceof HTMLAnchorElement
          ? this.#viewAll
          : this.#viewAll.querySelector('a');
      if (link && collectionUrl) link.href = collectionUrl;
      this.#viewAll.hidden = productsCount <= maxProducts;
    }
  }
}

if (!customElements.get('tabs-controller')) {
  customElements.define('tabs-controller', TabsController);
}
